"use client";

import { BackIcon } from "@/assets/icons";
import Button from "@/component/Button";
import { ThreadListItem } from "@/component/ThreadListItem";
import { ThreadListItemSkeleton } from "@/component/ThreadListItem/skeleton";
import { threadStore } from "@/store/thread";
import { userStore } from "@/store/user";
import "@ant-design/v5-patch-for-react-19";
import { useRouter } from "@bprogress/next/app";
import { Avatar, Dropdown } from "antd";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import styles from "./page.module.css";

export default observer(function ThreadListPage() {
  const user = userStore.user;
  const router = useRouter();
  const threads = threadStore.threads;
  const [hydrating, setHydrating] = useState(!threads.length);

  useEffect(() => {
    userStore.fetchUser().catch((error) => {
      console.error("Failed to fetch user:", error);
    });
    let cancelled = false;
    const loadThreads = async () => {
      setHydrating(true);
      try {
        await threadStore.fetchThreads();
      } catch (error) {
        console.error("Failed to fetch threads:", error);
      } finally {
        if (!cancelled) {
          setHydrating(false);
        }
      }
    };

    loadThreads();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  return (
    <div className="relative min-h-screen w-full bg-background-container">
      <header className="sticky top-0 h-[60px] px-5 sm:px-11 flex items-center justify-between border-b border-b-[rgba(16,16,16,0.12)] backdrop-blur-sm z-[10] bg-background-elevated/80 text-text-primary">
        <Button
          type="plain"
          className="text-xl"
          onClick={handleBack}
          icon={<BackIcon className="w-4 h-4" />}
        >
          <span>Back to start</span>
        </Button>

        {user && (
          <Dropdown
            trigger={["click"]}
            overlayClassName={styles.userMenu}
            menu={{
              items: [
                {
                  label: (
                    <a href="/auth/logout">
                      <div className="flex items-center gap-2 font-medium leading-[18px]">
                        Sign Out
                      </div>
                    </a>
                  ),
                  key: "logout",
                },
              ],
            }}
          >
            <div className="flex items-center gap-3 cursor-pointer transition-opacity duration-200 hover:opacity-80">
              <Avatar size={36} src={user.avatarUrl} className="rounded-full" />
              <span className="text-text-primary text-sm">{user.nickname}</span>
            </div>
          </Dropdown>
        )}
      </header>

      <div className="w-full px-5 sm:px-11 relative pt-10 pb-20 mx-auto">
        <h1 className="text-text-primary text-xl font-bold mb-5">
          Recent projects
        </h1>

        {hydrating || threadStore.threadsLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-5 gap-x-3 gap-y-6">
            {Array.from({ length: 8 }).map((_, idx) => (
              <ThreadListItemSkeleton key={idx} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-5 gap-x-3 gap-y-6">
            {threads.map((item) => (
              <Link href={`/thread/${item.id}`} key={item.id}>
                <ThreadListItem thread={item} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
